import { useState, type ReactElement } from 'react';
import { ApiRoutes } from '@gr/core/api-routes';
import { MediaKind } from '@gr/core/content/media/formats';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { Link, useNavigate, useSearchParams, useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button, LinkButton } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { SplitLayout } from '../../../ui-kit/SplitLayout/SplitLayout';
import { useBlog } from '../../accounts/workspaces';
import { MediaFacts } from './MediaFacts/MediaFacts';
import { MediaNoteForm } from './MediaNoteForm/MediaNoteForm';
import { MediaThumb } from './MediaThumb/MediaThumb';
import { PostLinks } from './PostLinks/PostLinks';
import styles from './MediaPage.module.scss';

// MED-UC-3, MED-UC-4, MED-UC-5, MED-UC-5.CC-1, MED-UC-3.CC-1
export function MediaPage({ mediaId }: { mediaId: string }): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog, can } = useBlog();
  const navigate = useNavigate();
  const confirm = useConfirm();
  // MED-UC-3.CC-1: карточку открыли из просмотра медиа поста — к нему можно вернуться.
  const fromPost = useSearchParams().get('post');
  const media = useResource(() => api.media.get(blog.id, mediaId), [api, blog.id, mediaId]);
  const remove = useAction(() => api.media.delete(blog.id, mediaId));
  const [blockers, setBlockers] = useState<{ id: string; title: string }[]>([]);
  const editable = can(BlogPermission.EditContent);

  if (media.loading && !media.data) return <Loading label={t('common.loading')} />;
  if (media.error || !media.data) return <Alert tone="danger">{te(media.error)}</Alert>;
  const item = media.data;
  const original = ApiRoutes.media.original(blog.id, item.id);

  const onDelete = async (): Promise<void> => {
    if (!(await confirm({ title: t('media.card.deleteConfirm', { name: item.originalName }), confirmLabel: t('common.delete'), danger: true }))) return;
    const result = await remove.run();
    if (result.success) navigate('media', { blogId: blog.id });
    else setBlockers(result.data ?? []);
  };

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={item.originalName}
        subtitle={
          <span className="u-row">
            <Link to="media" params={{ blogId: blog.id }}>{t('media.card.back')}</Link>
            {fromPost ? <BackToPost blogId={blog.id} postId={fromPost} title={item.posts.find((p) => p.id === fromPost)?.title} /> : null}
          </span>
        }
        actions={
          <>
            <LinkButton href={original} icon="download" download>
              {t('media.card.downloadOriginal')}
            </LinkButton>
            {editable ? (
              <Button variant="ghost" icon="trash" loading={remove.pending} onClick={() => void onDelete()}>
                {t('common.delete')}
              </Button>
            ) : null}
          </>
        }
      />
      {remove.error ? (
        <Alert tone="danger" title={te(remove.error)}>
          <PostLinks blogId={blog.id} posts={blockers} />
        </Alert>
      ) : null}
      <SplitLayout variant="media">
        {item.kind === MediaKind.Video && item.ext === 'mp4' ? (
          <video className={styles.video} controls preload="metadata" src={original} aria-label={item.originalName} />
        ) : (
          <MediaThumb media={item} />
        )}
        <>
          <Card>
            <MediaFacts media={item} timezone={blog.timezone} />
          </Card>
          <Card title={t('media.card.note')}>
            <MediaNoteForm
              note={item.note}
              editable={editable}
              onSubmit={async (note) => {
                const result = await api.media.updateNote(blog.id, mediaId, note);
                if (result.success) media.setData(result.data);
                return result;
              }}
            />
          </Card>
          <Card title={t('media.card.posts')}>
            <PostLinks blogId={blog.id} posts={item.posts} empty={t('media.card.notUsed')} />
          </Card>
        </>
      </SplitLayout>
    </div>
  );
}

function BackToPost({ blogId, postId, title }: { blogId: string; postId: string; title: string | undefined }): ReactElement {
  const { t } = useT();
  return (
    <Link to="post" params={{ blogId, postId }}>
      {title ? t('media.card.backToPost', { title }) : t('media.card.backToPostUntitled')}
    </Link>
  );
}
